/* MUI */
import { Box, Typography } from '@mui/material';

/*  Shared  */
import { FontSizes } from 'application/Shared/Constants';
import { Helpers, Notify } from 'application/Shared/Helpers';
import { debug } from 'application/Shared/Utils';

/*  React  */
import { TFunction } from 'i18next';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

/*  Components  */
import { DataVisualCard } from './DataVisualCard';

export const enum FormatterType {
    FILESIZE = 'fileSize',
    NONE = 'none',
    TIME = 'time'
}
interface FormatValueProps {
    value: number;
    formatter: FormatterType;
}

const formatValue = ({
    value,
    formatter
}: FormatValueProps): string | number | null => {
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = parseInt(
        Math.floor(Math.log(value) / Math.log(1024)).toString(),
        10
    );
    const days = Math.floor(value / 86400);
    const hours = Math.floor(value / 3600);
    const minutes = Math.floor(value / 60);
    const seconds = value;

    switch (formatter) {
        case FormatterType.FILESIZE:
            if (value === 0) return '0B';
            if (value < 0 || Number.isNaN(i)) return null;
            return `${Math.round(value / 1024 ** i)} ${sizes[i]}`;
        case FormatterType.TIME:
            if (days > 0) return `${days} Days`;
            if (hours > 0) return `${hours} Hr`;
            if (minutes > 0) return `${minutes} Min`;
            if (seconds > 0) return `${seconds} Sec`;
            return value;
        default:
            return value;
    }
};

interface CalculateSumProps {
    countVar: string;
    data: unknown[];
    t: TFunction<'translation', undefined>;
}

const calculateSum = ({ countVar, data, t }: CalculateSumProps): number => {
    let sum = 0;

    if (!data?.length) {
        return 0;
    }

    Object.values(data).forEach((row) => {
        if (!row) {
            return;
        }
        try {
            // a is the whole object, b is the key
            // We get four types of sliceVal here,
            // the one where a[b] => object[googleSignInActivity] = true/false
            // the one where a[b] => object[usage] = 1256
            // the one where a[b] => object[usage] = null
            // the one where a[b] => object[anotherKey] = "1256"
            const sliceVal = countVar
                .split('.')
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                .reduce((a: Record<string, any>, b: string) => a[b], row);
            if (typeof sliceVal === 'boolean' && sliceVal === true) {
                sum += 1;
            }
            // parsing boolean.toString will give a NaN
            const parsedValue = sliceVal && parseFloat(sliceVal.toString());
            if (parsedValue && Helpers.isNum(parsedValue)) {
                sum += parsedValue;
            }
        } catch (e) {
            debug.e('Error Calculating Sum');
            Notify.e(t('Common.error'));
        }
    });

    return sum;
};

interface DataAPIVisualsSumProps {
    countVar: string;
    data: unknown[];
    filteredResults?: boolean;
    formatter?: FormatterType;
    icon?: React.ReactNode;
    loading?: boolean;
    noDataMessage?: string;
    subtitle?: string;
    title: string;
    tooltip?: string;
}

export const DataAPIVisualsSum: React.FC<DataAPIVisualsSumProps> = ({
    countVar,
    data,
    filteredResults,
    formatter = FormatterType.NONE,
    icon,
    loading,
    noDataMessage,
    subtitle,
    title,
    tooltip
}: DataAPIVisualsSumProps) => {
    const { t } = useTranslation();
    const [sum, setSum] = useState<string | number | null>(0);

    useEffect(() => {
        let calculatedSum = calculateSum({ countVar, data, t }) as
            | string
            | number
            | null;
        if (formatter) {
            calculatedSum = formatValue({
                formatter,
                value: calculatedSum as number
            });
        }
        setSum(calculatedSum);
    }, [countVar, data, formatter, sum, t]);

    return (
        <DataVisualCard
            filteredResults={filteredResults}
            icon={icon}
            loading={loading}
            subtitle={subtitle}
            title={title}
            tooltip={tooltip}
        >
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                style={{
                    fontSize: FontSizes[5],
                    minHeight: '120px',
                    textAlign: 'center'
                }}
                data-testid="dataAPIVisualsSumBox"
                data-trackid="dataAPIVisualsSumBox"
                height="100%"
                width="100%"
            >
                {sum ? (
                    <Typography color="text.cardKPI" variant="h1">
                        {sum}
                    </Typography>
                ) : (
                    <Typography
                        variant="body2"
                        color="text.cardKPINoData"
                        sx={{ fontSize: FontSizes[2] }}
                    >
                        {noDataMessage || t('Common.noData')}
                    </Typography>
                )}
            </Box>
        </DataVisualCard>
    );
};
